import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { BookOpen, Coins, History, Home, Scale } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import ProviderHeader from 'components/ProviderHeader';
import { readLastVariant } from 'lib/variant-assets';
import { rulesPathForVariant } from 'lib/variant-rules';

interface FooterLink {
  to: string;
  icon: LucideIcon;
  label: string;
  sublabel: string;
  end?: boolean;
  desktopOnly?: boolean;
}

export default function Layout() {
  const location = useLocation();
  const isTableRoute = location.pathname.startsWith('/table/');
  const isLobbyRoute = location.pathname.startsWith('/lobby');
  const rulesPath = rulesPathForVariant(readLastVariant());

  if (isTableRoute) {
    return <Outlet />;
  }

  const footerLinks: FooterLink[] = [
    { to: '/home', icon: Home, label: 'Home', sublabel: 'Play', end: true, desktopOnly: true },
    { to: rulesPath, icon: BookOpen, label: 'Rules', sublabel: 'Guide' },
    { to: '/transactions', icon: Coins, label: 'Wallet', sublabel: 'History' },
    { to: '/hand-history', icon: History, label: 'Hands', sublabel: 'Results' },
    { to: '/terms', icon: Scale, label: 'Terms', sublabel: 'Legal', end: true },
  ];

  const mobileLinks = footerLinks.filter((link) => !link.desktopOnly);

  const navItemClass = ({ isActive }: { isActive: boolean }) =>
    `provider-footer-item${isActive ? ' provider-footer-item-active' : ''}`;

  return (
    <div
      className={
        isLobbyRoute
          ? 'provider-shell provider-shell--lobby relative flex h-dvh max-h-dvh flex-col overflow-hidden text-white'
          : 'provider-shell relative flex h-dvh max-h-dvh flex-col overflow-hidden text-white'
      }
    >
      <div className="provider-home-bg pointer-events-none absolute inset-0" aria-hidden />
      <div className="provider-home-vignette pointer-events-none absolute inset-0" aria-hidden />

      <ProviderHeader homeLinkTo="/home" />

      <main className="provider-main-scroll relative z-10 mx-auto w-full max-w-[1400px] min-h-0 flex-1 overflow-y-auto overscroll-y-auto px-4 pt-4 sm:px-8 lg:px-10 md:pt-6">
        <Outlet />
      </main>

      {/* Mobile bottom navigation */}
      <nav className="provider-bottom-nav md:hidden" aria-label="App navigation">
        <div className="provider-footer provider-bottom-nav-inner">
          <div className="provider-footer-grid provider-footer-grid-mobile">
            {mobileLinks.map(({ to, icon: Icon, label, sublabel, end }) => (
              <NavLink key={to} to={to} end={end} className={navItemClass}>
                <Icon size={20} className="text-tp-gold" strokeWidth={2.25} />
                <span className="provider-footer-label">{label}</span>
                <span className="provider-footer-sublabel">{sublabel}</span>
              </NavLink>
            ))}
          </div>
        </div>
      </nav>

      {/* Desktop footer */}
      <footer className="provider-footer provider-footer-desktop relative z-10 hidden md:block">
        <div className="mx-auto max-w-[1400px] px-4 py-5 sm:px-8 lg:px-10">
          <div className="provider-footer-grid provider-footer-grid-desktop">
            {footerLinks.map(({ to, icon: Icon, label, sublabel, end }) => (
              <NavLink key={to} to={to} end={end} className={navItemClass}>
                <Icon size={22} className="text-tp-gold" strokeWidth={2.25} />
                <span className="provider-footer-label">{label}</span>
                <span className="provider-footer-sublabel">{sublabel}</span>
              </NavLink>
            ))}
          </div>
          <p className="provider-footer-tagline mt-4 text-center text-xs text-white/35">
            Teen Patti · Play responsibly
          </p>
        </div>
      </footer>
    </div>
  );
}
